/**
 * PR-E5 — SHX → line-segment cache for the Three.js scene.
 *
 * SHX fonts are inherently vector: each glyph is a list of straight-line
 * + circular-arc strokes recorded in font units. Unlike TTF, where SDF
 * atlases are the right data structure, SHX has no bitmap representation
 * and any rasterization (e.g. msdf-bmfont) destroys the precise stroke
 * topology AutoCAD relies on. So instead of forcing SHX into the SDF
 * pipeline, this module flattens each glyph into an array of straight
 * `LineSegment`s (arcs polyline-approximated to a fixed angular budget)
 * which the scene assembles into a single `THREE.LineSegments` mesh per
 * SHX-fonted entity.
 *
 * # Data flow
 *
 *   ShxFont (parsed via @dxf-ai-editor/shx-parser)
 *     └──► flattenShxGlyphToSegments(glyph)
 *           └──► LineSegment[] (in font-unit space, glyph origin at (0,0))
 *
 * The renderer (textRunRenderer.renderShxTextRun) walks a string,
 * looks up each glyph's segments, applies tracking + width-factor +
 * oblique shear + height-scale, and emits a flat Float32 vertex pair
 * buffer ready for `LineSegmentsGeometry` / `THREE.LineSegments`.
 *
 * # Caching strategy
 *
 *   - One `ShxStrokeFont` per font name, lazily built on first call to
 *     `loadShxFontAsLineGroups(name)`.
 *   - The shape of `ShxStrokeFont` mirrors `SdfAtlas` enough that a
 *     `FontResource` consumer can branch by `.kind`.
 *   - Cache is module-scoped so a re-render of the scene doesn't re-flatten
 *     the same font twice; the eviction helper is exposed for tests only.
 *
 * # Q15.C fallback
 *
 * If `FontRegistry` cannot resolve `name` to an SHX entry, this module
 * rejects with `MissingShxFontError` so the Scene3DStage caller can fall
 * back to "?????" placeholder rectangles, matching the SDF Q15.C path
 * (PR-E2).
 *
 * # Arc → polyline approximation
 *
 * SHX strokes can include circular arcs (opcodes 0x08, 0x09, 0x0A, 0x0B
 * in the parser). Three.js `LineSegments2` does not natively render
 * arcs, so we tessellate each arc into a polyline with `ARC_SEGMENT_DEG`
 * angular resolution (default 6° / arc-segment ≈ 60-pt budget for a
 * full circle). 6° was picked to stay below sub-pixel error at 100%
 * zoom on a 1080p viewport for typical SHX glyph radii (≤ 30 font
 * units → ≤ 1.5 px arc-chord deviation). Lower values look sharper at
 * extreme zoom but quadratically inflate the line-segment buffer.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1 — full fidelity
 *
 * No glyph is silently dropped. Empty-stroke glyphs (e.g. space) keep
 * their `advanceX` so cursor advance is preserved. Missing glyphs fall
 * back to `?` (codepoint 0x3f) per AutoCAD convention; if `?` is also
 * absent, the renderer emits an empty-segment glyph whose advanceX
 * matches the font's average advance so the run width does not collapse
 * to zero.
 */

import type { ShxFont, ShxGlyph, ShxStroke } from "@dxf-ai-editor/shx-parser";

import { FontRegistry } from "../../fonts/fontRegistry";

// ── Public types ─────────────────────────────────────────────────

/**
 * One straight line segment in font-unit space (origin at glyph (0,0),
 * +Y up). Curved SHX strokes are tessellated into a sequence of these.
 */
export interface LineSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** Per-glyph stroke record. */
export interface ShxGlyphStrokes {
  /** Flattened straight-line segments (arcs already polylined). */
  segments: LineSegment[];
  /** Cursor advance after this glyph (font units). */
  advanceX: number;
  /** Vertical advance — non-zero for vertical-mode SHX fonts. */
  advanceY: number;
}

/**
 * Loaded SHX font in renderer-friendly shape. Mirrors the loose shape
 * of `SdfAtlas` so the FontRegistry consumer can branch on `kind`.
 *
 * `lineHeight` is set to `above + below` from the SHX header — that is
 * what `renderShxText` uses as the canonical baseline-to-baseline
 * distance. Callers scale `runHeight / above` to convert font units to
 * world units (matching the existing SVG path).
 */
export interface ShxStrokeFont {
  fontName: string;
  /** SHX header `above` in font units — baseline → cap top. */
  above: number;
  /** SHX header `below` in font units — baseline → descender bottom. */
  below: number;
  /** Convenience: `above + below` (font units). */
  lineHeight: number;
  /** Codepoint → strokes. */
  glyphs: Map<number, ShxGlyphStrokes>;
}

/** Raised when the SHX font is missing from the FontRegistry. */
export class MissingShxFontError extends Error {
  readonly fontName: string;
  readonly cause?: unknown;
  constructor(fontName: string, message: string, cause?: unknown) {
    super(`[shxStrokeAtlas] ${fontName}: ${message}`);
    this.name = "MissingShxFontError";
    this.fontName = fontName;
    if (cause !== undefined) this.cause = cause;
  }
}

// ── Cache (one promise per font name) ─────────────────────────────

const cache = new Map<string, Promise<ShxStrokeFont>>();

// ── Tunables ─────────────────────────────────────────────────────

/**
 * Angular step (in degrees) used when tessellating SHX arcs into
 * polylines. 6° gives ~60 segments per full circle which is more than
 * enough for typical SHX glyph radii (≤ 30 font units) at 100% zoom on
 * a 1080p viewport. The renderer uses `LineSegments2` so each step
 * adds two Float32 vertices to the buffer.
 *
 * Exposed as a constant so tests can pin the chord error.
 */
export const ARC_SEGMENT_DEG = 6;

const ARC_SEGMENT_RAD = (ARC_SEGMENT_DEG * Math.PI) / 180;

// ── Public API ───────────────────────────────────────────────────

/**
 * Load (or return the cached promise of) an SHX stroke font for
 * `fontName`. The font must already be present in `FontRegistry` —
 * the registry is the source of truth for both the SVG-renderer path
 * and the Three.js-renderer path so duplicate fetching is avoided.
 *
 * @throws {MissingShxFontError} if `FontRegistry.getShx(fontName)`
 *   returns `undefined`. Caller (Scene3DStage) catches this and
 *   emits the Q15.C "?????" placeholder.
 */
export function loadShxFontAsLineGroups(
  fontName: string,
): Promise<ShxStrokeFont> {
  const existing = cache.get(fontName);
  if (existing) return existing;

  // We wrap the synchronous flatten in a Promise so the public API
  // matches `loadSdfAtlas` (which is genuinely async — atlases live on
  // disk). Callers can then `Promise.all([sdf, shx])` in dispatch
  // logic without special-casing.
  const promise = (async (): Promise<ShxStrokeFont> => {
    const shxFont = FontRegistry.getShx(fontName);
    if (!shxFont) {
      throw new MissingShxFontError(
        fontName,
        "FontRegistry has no SHX entry for this name; user must upload the font",
      );
    }
    return buildShxStrokeFont(shxFont);
  })().catch((err) => {
    // Mirror sdfAtlasLoader's eviction behaviour so a fixed-up registry
    // can be retried on the next render pass without a manual evict.
    cache.delete(fontName);
    throw err;
  });

  cache.set(fontName, promise);
  return promise;
}

/**
 * Test-only: clear the in-memory cache so each test starts from scratch.
 * Production code MUST NOT call this — flattening a 256-glyph font
 * costs ~5ms and the result is stable for the page lifetime.
 */
export function evictShxStrokeCache(): void {
  cache.clear();
}

/**
 * Test-only: peek at whether a font is cached without forcing a load.
 */
export function isShxStrokeCached(fontName: string): boolean {
  return cache.has(fontName);
}

// ── Internal: build the stroke font ───────────────────────────────

/**
 * Convert one parsed `ShxFont` into a `ShxStrokeFont`. Pure synchronous
 * function; exposed so unit tests can exercise the stroke flattening
 * directly without going through the cache.
 */
export function buildShxStrokeFont(font: ShxFont): ShxStrokeFont {
  const glyphs = new Map<number, ShxGlyphStrokes>();
  for (const [codepoint, glyph] of font.glyphs) {
    glyphs.set(codepoint, flattenShxGlyphToSegments(glyph));
  }
  return {
    fontName: font.name,
    above: font.above,
    below: font.below,
    lineHeight: font.above + font.below,
    glyphs,
  };
}

/**
 * Flatten one `ShxGlyph` into a list of straight line segments. Arcs
 * are tessellated to `ARC_SEGMENT_DEG` resolution. Lines are passed
 * through unchanged. Exposed so unit tests can exercise the
 * tessellation directly without round-tripping through `FontRegistry`.
 */
export function flattenShxGlyphToSegments(glyph: ShxGlyph): ShxGlyphStrokes {
  const segments: LineSegment[] = [];
  for (const stroke of glyph.strokes) {
    if (stroke.kind === "line") {
      // Skip degenerate zero-length segments — they bloat the buffer
      // without contributing pixels. AutoCAD-emitted SHX rarely has
      // these but synthetic test fonts do.
      if (stroke.x1 === stroke.x2 && stroke.y1 === stroke.y2) continue;
      segments.push({
        x1: stroke.x1,
        y1: stroke.y1,
        x2: stroke.x2,
        y2: stroke.y2,
      });
    } else {
      tessellateArc(stroke, segments);
    }
  }
  return {
    segments,
    advanceX: glyph.advanceX,
    advanceY: glyph.advanceY,
  };
}

/**
 * Tessellate a circular arc into a polyline of `ARC_SEGMENT_DEG`
 * angular steps. Pushes the resulting line segments onto `out` so the
 * caller can append into one buffer without intermediate allocations.
 *
 * The arc may be CCW or CW. We always walk from `startAngle` to
 * `endAngle` in the direction the parser recorded — flipping CW arcs
 * to CCW is a UV-only optimisation that doesn't apply to line
 * segments (each segment already carries its own direction).
 */
function tessellateArc(
  stroke: Extract<ShxStroke, { kind: "arc" }>,
  out: LineSegment[],
): void {
  const { cx, cy, r, startAngle, endAngle, ccw } = stroke;
  if (!Number.isFinite(r) || r <= 0) return;

  // Compute total angular sweep. Mirrors `arcSweep` in
  // packages/shx-parser/src/svgPath.ts but inlined to avoid pulling
  // the SVG path module into the scene3d bundle.
  const twoPi = Math.PI * 2;
  let raw = ccw ? endAngle - startAngle : startAngle - endAngle;
  raw = raw % twoPi;
  if (raw < 0) raw += twoPi;
  // Zero-sweep arc: nothing to draw. (A "full circle" SHX arc encodes
  // the sweep as 2π — our parser already handles that case.)
  if (raw < 1e-9) return;

  const totalSweep = raw;
  const stepCount = Math.max(2, Math.ceil(totalSweep / ARC_SEGMENT_RAD));
  const stepSize = totalSweep / stepCount;

  // Walk forward in the recorded direction. For CCW we add stepSize;
  // for CW we subtract.
  const sign = ccw ? 1 : -1;
  let angle = startAngle;
  let prevX = cx + r * Math.cos(angle);
  let prevY = cy + r * Math.sin(angle);
  for (let i = 1; i <= stepCount; i++) {
    angle = startAngle + sign * stepSize * i;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    out.push({ x1: prevX, y1: prevY, x2: x, y2: y });
    prevX = x;
    prevY = y;
  }
}
